"use client";

import { useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";
// NOTE: not using the high-level DialogContent from @baluu/ui here.
// DialogContent (packages/ui/src/components/dialog.tsx) hardcodes its own
// <DialogPortal> with no way to redirect the underlying Radix portal to a
// container, so it always escapes to document.body — outside our shadow
// root and therefore unstyled. The lower-level exports (Dialog, DialogPortal,
// DialogOverlay) DO forward props straight to their Radix primitives, so we
// compose those directly with DialogPrimitive.Content, the same way
// SheetContent composes SheetPortal/SheetPrimitive.Content with a `container`
// prop in sheet.tsx — this keeps the dialog inside the shadow root while
// gaining Radix's real Escape-to-close and click-outside-to-close instead of
// hand-rolled overlay/panel markup.
import { Dialog, DialogOverlay, DialogPortal, DialogTitle } from "@baluu/ui/components/dialog";
import { Input } from "@baluu/ui/components/input";
import { Button } from "@baluu/ui/components/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@baluu/ui/components/input-otp";
import { cn } from "@baluu/ui/lib/utils";
import { getAuthClient, completeSignIn } from "~/widgets/lib/auth-client";
import { useShadowPortal } from "~/widgets/lib/use-shadow-portal";
import { BOOKING_PORTAL_IDS } from "@baluu/ui/lib/checkout/portals";
import type { AuthUser } from "~/widgets/stores/auth-store";

export type CustomerAuthDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  apiUrl?: string;
  businessSlug?: string;
  schoolName?: string;
  primaryColor?: string;
  onSignedIn?: (user: AuthUser) => void;
};

const ORIGIN_NOT_ALLOWED =
  "Sign-in isn't available on this website yet.";

export function CustomerAuthDialog(p: CustomerAuthDialogProps) {
  const mount = useShadowPortal({ open: p.open, id: BOOKING_PORTAL_IDS.auth, primaryColor: p.primaryColor });
  const [step, setStep] = useState<"email" | "otp">("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const client = getAuthClient(p.apiUrl, p.businessSlug);

  function networkBlocked(err: unknown) {
    // A disallowed origin never gets CORS headers: the browser reports a
    // network failure (status 0 / TypeError), not an API error body.
    const status = (err as any)?.status;
    return status === 0 || status === undefined || err instanceof TypeError;
  }

  async function sendCode() {
    setIsLoading(true); setError("");
    try {
      const { error } = await client.emailOtp.sendVerificationOtp({ email: email.trim(), type: "sign-in" });
      if (error) {
        if (networkBlocked(error)) {
          console.warn(`[Baluu Login] ${window.location.origin} is not an allowed website for customer login. Add it in Baluu → Web components → Customer login.`);
          setError(ORIGIN_NOT_ALLOWED);
        } else setError(error.message || "Failed to send OTP. Please try again.");
        return;
      }
      setStep("otp");
    } catch (err) {
      setError(networkBlocked(err) ? ORIGIN_NOT_ALLOWED : "Failed to send OTP. Please try again.");
    } finally { setIsLoading(false); }
  }

  async function verify() {
    setIsLoading(true); setError("");
    try {
      const { error } = await client.signIn.emailOtp({ email: email.trim(), otp });
      if (error) { setError(error.message || "Invalid OTP. Please try again."); return; }
      const user = await completeSignIn(p.apiUrl, p.businessSlug);
      if (!user) { setError("Failed to verify OTP. Please try again."); return; }
      p.onSignedIn?.(user);
      p.onOpenChange(false);
      setStep("email"); setOtp("");
    } catch {
      setError("Failed to verify OTP. Please try again.");
    } finally { setIsLoading(false); }
  }

  if (!p.open || !mount) return null;

  return (
    <Dialog open={p.open} onOpenChange={p.onOpenChange}>
      <DialogPortal container={mount}>
        <DialogOverlay />
        <DialogPrimitive.Content
          // Radix's Content wires up role="dialog" and aria-labelledby (via
          // DialogTitle below) automatically, but this version does not set
          // aria-modal itself — that one manual attribute is still needed.
          aria-modal="true"
          className={cn(
            "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed z-50 grid w-full max-w-[calc(100%-2rem)] gap-4 rounded-lg border p-6 shadow-lg duration-200 sm:max-w-sm",
          )}
          style={{ top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}
        >
          <DialogTitle className="pr-6">
            {step === "email" ? "Sign in" : "Enter your code"}
          </DialogTitle>
          <DialogPrimitive.Close
            aria-label="Close"
            className="ring-offset-background focus:ring-ring absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden"
          >
            <XIcon className="size-4" />
          </DialogPrimitive.Close>
          {step === "email" ? (
            <form onSubmit={(e) => { e.preventDefault(); void sendCode(); }} className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {p.schoolName ? `Sign in to ${p.schoolName}. ` : ""}We'll email you a one-time code.
              </p>
              <Input type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button type="submit" disabled={isLoading || !email.trim()} className="w-full">{isLoading ? "Sending…" : "Send code"}</Button>
            </form>
          ) : (
            <form onSubmit={(e) => { e.preventDefault(); void verify(); }} className="space-y-3">
              <p className="text-sm text-muted-foreground">We sent a 6-digit code to {email}. Code expires in 10 minutes.</p>
              <InputOTP maxLength={6} value={otp} onChange={setOtp} autoFocus>
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button type="submit" disabled={isLoading || otp.length !== 6} className="w-full">{isLoading ? "Verifying…" : "Verify"}</Button>
              <div className="flex justify-between text-sm">
                <button type="button" className="underline" onClick={() => { setStep("email"); setOtp(""); setError(""); }}>Change email</button>
                <button type="button" className="underline" disabled={isLoading} onClick={() => void sendCode()}>Resend code</button>
              </div>
            </form>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}

export default CustomerAuthDialog;
